import { Link } from "@tanstack/react-router"
import { useState, useEffect, useRef } from "react"
import { Menu, X, ChevronDown, ArrowRight, Home } from "lucide-react"
import { Button } from "@/components/ui/button"
import { ThemeToggle } from "@/components/theme-toggle"

const navItems = [
  {
    label: "Product",
    href: "/features",
    children: [
      { label: "All Features", href: "/features", description: "Platform overview" },
      { label: "Integrations", href: "/features/integrations", description: "2000+ connections" },
      { label: "AI Gateway", href: "/features/ai-gateway", description: "LLM proxy & management" },
      { label: "Agents Platform", href: "/features/agents-platform", description: "Build AI agents" },
      { label: "Security", href: "/features/security", description: "Zero-trust architecture" },
      { label: "Analytics", href: "/features/analytics", description: "Real-time insights" },
    ]
  },
  {
    label: "Solutions",
    href: "/use-cases",
    children: [
      { label: "All Solutions", href: "/use-cases", description: "Use case gallery" },
      { label: "API Management", href: "/use-cases/api-management", description: "Unified API control" },
      { label: "AI Automation", href: "/use-cases/ai-automation", description: "Intelligent workflows" },
      { label: "Data Sync", href: "/use-cases/data-synchronization", description: "Real-time sync" },
      { label: "FinOps", href: "/use-cases/finops", description: "Cost optimization" },
      { label: "Enterprise Security", href: "/use-cases/enterprise-security", description: "Compliance & audit" },
    ]
  },
  {
    label: "Resources",
    href: "/concepts",
    children: [
      { label: "Concepts", href: "/concepts", description: "Learn the fundamentals" },
      { label: "Comparisons", href: "/comparisons", description: "How we compare" },
      { label: "API Gateway Guide", href: "/concepts/api-gateway", description: "Gateway deep-dive" },
      { label: "AI Agents Guide", href: "/concepts/ai-agents", description: "Agent architecture" },
    ]
  },
  { label: "Pricing", href: "/info/pricing" },
  { label: "About", href: "/info/about" },
]

export function Header() {
  const [isMenuOpen, setIsMenuOpen] = useState(false)
  const [isAuthenticated, setIsAuthenticated] = useState(false)
  const [openDropdown, setOpenDropdown] = useState<string | null>(null)
  const dropdownTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  useEffect(() => {
    const hasAccessToken = document.cookie.includes('matricks_access_token')
    const hasRefreshToken = document.cookie.includes('matricks_refresh_token')
    setIsAuthenticated(hasAccessToken || hasRefreshToken)
  }, [])

  // Prevent body scroll when menu is open
  useEffect(() => {
    if (isMenuOpen) {
      document.body.style.overflow = 'hidden'
    } else {
      document.body.style.overflow = ''
    }
    return () => {
      document.body.style.overflow = ''
    }
  }, [isMenuOpen])

  const handleMouseEnter = (label: string) => {
    if (dropdownTimeoutRef.current) {
      clearTimeout(dropdownTimeoutRef.current)
    }
    setOpenDropdown(label)
  }

  const handleMouseLeave = () => {
    dropdownTimeoutRef.current = setTimeout(() => {
      setOpenDropdown(null)
    }, 150)
  }

  useEffect(() => {
    return () => {
      if (dropdownTimeoutRef.current) {
        clearTimeout(dropdownTimeoutRef.current)
      }
    }
  }, [])

  return (
    <>
      <header className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
        <div className="container flex h-14 items-center justify-between px-6">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2 group">
            <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary transition-transform group-hover:scale-105">
              <span className="text-sm font-bold text-primary-foreground">M</span>
            </div>
            <span className="text-lg font-bold">Matricks.ai</span>
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center">
            {navItems.map((item) => (
              <div
                key={item.label}
                className="relative"
                onMouseEnter={() => item.children && handleMouseEnter(item.label)}
                onMouseLeave={handleMouseLeave}
              >
                {item.children ? (
                  <>
                    <Link
                      to={item.href}
                      className="flex items-center px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                    >
                      {item.label}
                      <ChevronDown className={`ml-1 h-3.5 w-3.5 transition-transform duration-200 ${openDropdown === item.label ? 'rotate-180' : ''}`} />
                    </Link>
                    <div
                      className={`absolute top-full left-0 mt-1 w-64 rounded-xl border bg-background/95 backdrop-blur shadow-lg transition-all duration-200 ${
                        openDropdown === item.label
                          ? 'opacity-100 translate-y-0 pointer-events-auto'
                          : 'opacity-0 -translate-y-2 pointer-events-none'
                      }`}
                      onMouseEnter={() => handleMouseEnter(item.label)}
                      onMouseLeave={handleMouseLeave}
                    >
                      <div className="p-2">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            className="flex flex-col rounded-lg px-3 py-2.5 hover:bg-muted transition-colors group/item"
                          >
                            <span className="text-sm font-medium group-hover/item:text-primary transition-colors">
                              {child.label}
                            </span>
                            {child.description && (
                              <span className="text-xs text-muted-foreground mt-0.5">
                                {child.description}
                              </span>
                            )}
                          </Link>
                        ))}
                      </div>
                    </div>
                  </>
                ) : (
                  <Link
                    to={item.href!}
                    className="px-3 py-2 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground"
                  >
                    {item.label}
                  </Link>
                )}
              </div>
            ))}
          </nav>

          {/* Right Side Actions */}
          <div className="flex items-center space-x-2">
            <ThemeToggle />
            <div className="hidden md:flex items-center space-x-2">
              {isAuthenticated ? (
                <Button size="sm" asChild>
                  <Link to="/overview">Dashboard</Link>
                </Button>
              ) : (
                <>
                  <Button variant="ghost" size="sm" asChild>
                    <Link to="/login">Sign In</Link>
                  </Button>
                  <Button size="sm" asChild>
                    <Link to="/login">Get Started</Link>
                  </Button>
                </>
              )}
            </div>

            {/* Mobile Menu Toggle */}
            <Button
              variant="ghost"
              size="icon"
              className="lg:hidden h-9 w-9"
              onClick={() => setIsMenuOpen(!isMenuOpen)}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
            >
              {isMenuOpen ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
            </Button>
          </div>
        </div>
      </header>

      {/* Full-Page Mobile Menu */}
      {isMenuOpen && (
        <div className="fixed inset-0 z-40 bg-background lg:hidden overflow-y-auto">
          {/* Mobile Menu Header */}
          <div className="sticky top-0 z-50 w-full border-b bg-background/95 backdrop-blur">
            <div className="container flex h-14 items-center justify-between px-6">
              <Link to="/" className="flex items-center space-x-2" onClick={() => setIsMenuOpen(false)}>
                <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-primary">
                  <span className="text-sm font-bold text-primary-foreground">M</span>
                </div>
                <span className="text-lg font-bold">Matricks.ai</span>
              </Link>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9"
                onClick={() => setIsMenuOpen(false)}
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </Button>
            </div>
          </div>

          {/* Mobile Menu Content */}
          <div className="container px-6 py-6">
            {/* Home Link */}
            <Link
              to="/"
              className="flex items-center gap-2 py-3 text-sm font-medium hover:text-primary transition-colors"
              onClick={() => setIsMenuOpen(false)}
            >
              <Home className="h-4 w-4" />
              Home
            </Link>

            <div className="h-px bg-border my-4" />

            <div className="space-y-6">
              {navItems.map((item) => (
                <div key={item.label}>
                  {item.children ? (
                    <div>
                      <Link
                        to={item.href}
                        className="flex items-center justify-between py-2 text-sm font-semibold"
                        onClick={() => setIsMenuOpen(false)}
                      >
                        {item.label}
                        <ArrowRight className="h-4 w-4 text-muted-foreground" />
                      </Link>
                      <div className="mt-2 ml-2 space-y-1 border-l-2 border-muted pl-4">
                        {item.children.map((child) => (
                          <Link
                            key={child.label}
                            to={child.href}
                            className="block py-2 text-sm text-muted-foreground hover:text-foreground transition-colors"
                            onClick={() => setIsMenuOpen(false)}
                          >
                            {child.label}
                          </Link>
                        ))}
                      </div>
                    </div>
                  ) : (
                    <Link
                      to={item.href!}
                      className="block py-2 text-sm font-semibold hover:text-primary transition-colors"
                      onClick={() => setIsMenuOpen(false)}
                    >
                      {item.label}
                    </Link>
                  )}
                </div>
              ))}
            </div>

            {/* Auth Buttons */}
            <div className="mt-8 pt-6 border-t space-y-3">
              {isAuthenticated ? (
                <Button asChild className="w-full">
                  <Link to="/overview" onClick={() => setIsMenuOpen(false)}>
                    Go to Dashboard
                    <ArrowRight className="ml-2 h-4 w-4" />
                  </Link>
                </Button>
              ) : (
                <>
                  <Button variant="outline" asChild className="w-full">
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}>Sign In</Link>
                  </Button>
                  <Button asChild className="w-full">
                    <Link to="/login" onClick={() => setIsMenuOpen(false)}>
                      Get Started
                      <ArrowRight className="ml-2 h-4 w-4" />
                    </Link>
                  </Button>
                </>
              )}
            </div>
          </div>
        </div>
      )}
    </>
  )
}
